import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, of, pipe, Subscription } from 'rxjs';
import { map, mapTo, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { LoadingSpinnerService } from '../../../../services/loading-spinner.service';
import { LoggerService } from '../../../../services/logger/logger.service';
import { NotificationService } from '../../../general/services/notification.service';
import { ShopSettingsService } from '../../../general/services/shop-settings.service';
import { OrderLineType } from '../../../order/models/order-line-type';
import { OrderStateService } from '../../../order/services/order-state.service';
import { ProductsStateService } from '../../../order/services/products-state.service';
import { PartnerRegistrationFormComponent } from '../../../partner/components/partner-registration-form/partner-registration-form.component';
import { PartnerStateService } from '../../../partner/services/partner-state.service';
import { SmartCardCommunicationService } from '../../../smart-card/services/smart-card-communication.service';
import { ScanCard } from '../../../vu/models/scan-card';
import { PartnerCodeLoginSteps } from '../../models/partner-code-login-steps';
import { ScanPartnerCodeInfo } from '../../models/scan-partner-code-info';
import { PartnerCodeStateService } from '../../services/partner-code-state.service';
declare let fbq: Function;

@Component({
  selector: 'app-partner-code-login',
  templateUrl: './partner-code-login.component.html',
  styleUrls: ['./partner-code-login.component.css']
})
export class PartnerCodeLoginComponent implements OnInit, OnDestroy {

  @Output()
  back = new EventEmitter();

  @Output()
  next = new EventEmitter();

  @Output()
  registerAsGuest = new EventEmitter();

  @Output()
  authorizationAsGuest = new EventEmitter();

  @Output()
  navigationVisibilityChange = new EventEmitter();

  @Input()
  allowGuestCardRegistration = true;

  @Input()
  allowGuestAuthorization = false;

  private _currentStep = PartnerCodeLoginSteps.Login;

  private _readCardSubscription: Subscription;

  private _registerSubscription: Subscription;
  private _registerCardSubscription: Subscription;
  private _partnerInitializeSubscription: Subscription;

  private _registerInProgress = false;

  constructor(
    protected partnerStateService: PartnerStateService,
    private shopSettingsService: ShopSettingsService,
    private partnerCodeStateService: PartnerCodeStateService,
    private loadingSpinnerService: LoadingSpinnerService,
    private orderStateService: OrderStateService,
    private productsStateService: ProductsStateService,
    private loggerService: LoggerService,
    private notificationService: NotificationService,
    private smartCardCommunicationService: SmartCardCommunicationService,
  ) {
  }

  ngOnInit(): void {
    this._setCurrentStep(this._getInitStep());
  }

  ngOnDestroy(): void {
    this.notificationService.hide(PartnerCodeLoginComponent.name);
    this._unsubscribeRegisterSubscription();
    this._unsubscribeRegisterCardSubscription();
    this._unsubscribePartnerInitializeSubscription();
    this.registerInProgress = false;
  }

  private _setCurrentStep(partnerCodeLoginStep: PartnerCodeLoginSteps): void {
    this._currentStep = partnerCodeLoginStep;
    this.navigationVisibilityChange.emit();
  }

  private _getInitStep(): PartnerCodeLoginSteps {

    if (this.scanPartnerCodeInfo && this.scanPartnerCodeInfo.partner != null) {
      return PartnerCodeLoginSteps.Login;
    }

    if (this.canSaleGuestCard && this.partnerCodeEnabled && this.allowGuestCardRegistration) {
      return PartnerCodeLoginSteps.HowToProcess;
    }

    if (this.partnerCodeEnabled && this.scanPartnerCodeInfo == null) {
      return PartnerCodeLoginSteps.ScanCard;
    }

    if (!this.allowedLogin) {
      return PartnerCodeLoginSteps.Register;
    }

    return PartnerCodeLoginSteps.Login;
  }

  get registerInProgress(): boolean {
    return this._registerInProgress;
  }

  set registerInProgress(value: boolean) {
    if (this._registerInProgress === value) {
      return;
    }
    this._registerInProgress = value;
    if (value) {
      this.loadingSpinnerService.show();
    } else {
      this.loadingSpinnerService.hide();
    }
  }

  get showNavigationBack(): boolean {
    return this._currentStep === PartnerCodeLoginSteps.HowToProcess;
  }

  get partnerExist(): boolean {
    return this.partnerStateService.loggedInPartner != null;
  }

  onNext(): void {
    this.next.emit();
  }

  onBack(): void {
    this.notificationService.hide(PartnerCodeLoginComponent.name);
    switch (this._currentStep) {
      case PartnerCodeLoginSteps.Login:
      case PartnerCodeLoginSteps.Register:
        if (this.partnerCodeEnabled) {
          this.scanPartnerCodeInfo = null;
          this._setCurrentStep(PartnerCodeLoginSteps.ScanCard);
          return;
        }
        break;
      case PartnerCodeLoginSteps.ScanCard:
        if (this.canSaleGuestCard && this.partnerCodeEnabled && this.allowGuestCardRegistration) {
          this._setCurrentStep(PartnerCodeLoginSteps.HowToProcess);
          return;
        }
        break;
      case PartnerCodeLoginSteps.GuestRegister:
        this.scanPartnerCodeInfo = null;
        this._abortProduceCard();
        this._setCurrentStep(PartnerCodeLoginSteps.HowToProcess);
        return;
    }

    this.back.emit();
  }

  register(): void {
    this._setCurrentStep(PartnerCodeLoginSteps.Register);
  }

  onRegisterAsGuest(): void {
    if (this.allowGuestAuthorization) {
      this.authorizationAsGuest.emit();
    } else {
      this.registerAsGuest.emit();
    }
  }

  switchToLogin(): void {
    this._setCurrentStep(PartnerCodeLoginSteps.Login);
  }

  onRegister(partnerRegistrationForm: PartnerRegistrationFormComponent): void {
    this._unsubscribeRegisterSubscription();
    this.registerInProgress = true;
    this._registerSubscription = of(true).pipe(
      switchMap(_ => {
        if (this.scanPartnerCodeInfo && this.scanPartnerCodeInfo.card && this.scanPartnerCodeInfo.partner) {
          return this.partnerStateService.registerPartnerAuthorization(
            this.scanPartnerCodeInfo.partner.id,
            partnerRegistrationForm.partner.email,
            partnerRegistrationForm.password.password
          );
        }
        return this.partnerStateService.createPartner(
          partnerRegistrationForm.partner,
          partnerRegistrationForm.password.password
        );
      }),
      switchMap(_ => {
        if (this.scanPartnerCodeInfo) {
          return this.partnerCodeStateService.registerCard(this.scanPartnerCodeInfo.card).pipe(
            tap(partner => { this.partnerStateService.reload(partner); })
          );
        }
        return of(_);
      })
    ).subscribe(
      result => {
        if (result) {
          this._trackFacebookAnalytics();
          this.onNext();
        } else {
          partnerRegistrationForm.error = 'Registration failed. Please try again later';
        }
        this.registerInProgress = false;
      },
      (error) => {
        partnerRegistrationForm.error = error.message;
        this.registerInProgress = false;
      }
    );
  }

  private _trackFacebookAnalytics(): void {
    if (this.shopSettingsService.facebookAnalyticsId) {
      const location = environment.odooDatabaseName;
      fbq('track', 'CompleteRegistration', {
        'location': location
      });
    }
  }
  private _unsubscribeRegisterSubscription(): void {
    if (this._registerSubscription) {
      this._registerSubscription.unsubscribe();
      this._registerSubscription = null;
    }
  }

  private _unsubscribeRegisterCardSubscription(): void {
    if (this._registerCardSubscription) {
      this._registerCardSubscription.unsubscribe();
      this._registerCardSubscription = null;
    }
  }

  private _unsubscribePartnerInitializeSubscription(): void {
    if (this._partnerInitializeSubscription) {
      this._partnerInitializeSubscription.unsubscribe();
      this._partnerInitializeSubscription = null;
    }
  }

  onLogin(): void {
    if (this.scanPartnerCodeInfo && this.scanPartnerCodeInfo.isNewCard) {
      this._unsubscribeRegisterCardSubscription();
      this.registerInProgress = true;
      this._registerCardSubscription = this.partnerCodeStateService.registerCard(
        this.scanPartnerCodeInfo.card, this.partnerCodeStateService.activateMode)
        .pipe(
          tap(partner => { this.partnerStateService.reload(partner); })
        )
        .subscribe(
          _ => {
            this.onNext();
            this.registerInProgress = false;
            this.partnerCodeStateService.activateMode = false;
            this.scanPartnerCodeInfo.isNewCard = false;
          },
          (error) => {
            this.registerInProgress = false;
          }
        );
      return;
    }
    this._unsubscribePartnerInitializeSubscription();
    this._partnerInitializeSubscription = this.partnerStateService.initialize().subscribe(
      _ => {
        this.onNext();
      }
    );
  }

  get isVuMode(): boolean {
    return this.shopSettingsService.isVUMode;
  }

  get scanPartnerCodeInfo(): ScanPartnerCodeInfo {
    return this.partnerCodeStateService.scanPartnerCodeInfo;
  }

  set scanPartnerCodeInfo(value: ScanPartnerCodeInfo) {
    this.partnerCodeStateService.scanPartnerCodeInfo = value;
  }

  onScanPartnerCode(scanPartnerCodeInfo: ScanPartnerCodeInfo): void {
    this.notificationService.hide(PartnerCodeLoginComponent.name);
    if (!scanPartnerCodeInfo || !scanPartnerCodeInfo.card) {
      return;
    }

    if (this.partnerCodeStateService.activateMode) {
      if (scanPartnerCodeInfo.partner) {
        this.notificationService.show(PartnerCodeLoginComponent.name, 'Card already assigned to customer!');
      } else {
        this.scanPartnerCodeInfo = scanPartnerCodeInfo;
        this._setCurrentStep(PartnerCodeLoginSteps.Login);
      }
      return;
    } else {
      if (scanPartnerCodeInfo.partner) {
        this.scanPartnerCodeInfo = scanPartnerCodeInfo;
        if (scanPartnerCodeInfo.partner.email) {
          this._setCurrentStep(PartnerCodeLoginSteps.Login);
        } else {
          this._setCurrentStep(PartnerCodeLoginSteps.Register);
        }
        return;
      }
    }

    if (this.partnerCodeAuthorization && !scanPartnerCodeInfo.card.personalNumber) {
      this.notificationService.show(PartnerCodeLoginComponent.name, 'Card read error!');
      return;
    }

    if (this.canSaleGuestCard || !this.allowedRegistration) {
      this.notificationService.show(PartnerCodeLoginComponent.name, 'Card not found!');
      return;
    }

    this.scanPartnerCodeInfo = scanPartnerCodeInfo;
    this._setCurrentStep(PartnerCodeLoginSteps.Register);
  }

  get partnerCodeAuthorization(): boolean {
    return this.shopSettingsService.shopSettings.partnerCodeAuthorization;
  }

  get partnerCodeEnabled(): boolean {
    return this.partnerCodeStateService.enabled;
  }

  get showScanPartnerCode(): boolean {
    return this._currentStep === PartnerCodeLoginSteps.ScanCard;
  }

  get allowedRegistration(): boolean {
    return this.shopSettingsService.allowedRegistration && !this.isVuMode;
  }

  get showChoiceAuthorizationType(): boolean {
    return this._currentStep === PartnerCodeLoginSteps.HowToProcess;
  }

  get showRegistrationForm(): boolean {
    return this._currentStep === PartnerCodeLoginSteps.Register || this._currentStep === PartnerCodeLoginSteps.GuestRegister;
  }

  get showLoginForm(): boolean {
    return this._currentStep === PartnerCodeLoginSteps.Login;
  }

  onExistUser(): void {
    this._setCurrentStep(PartnerCodeLoginSteps.ScanCard);
  }

  onGuestUser(): void {
    this._readCard();
  }

  private _unsubscribeReadCardSubscription(): void {
    if (this._readCardSubscription) {
      this._readCardSubscription.unsubscribe();
      this._readCardSubscription = null;
    }
  }

  private _readCard(): void {
    this.loadingSpinnerService.show();
    this._unsubscribeReadCardSubscription();
    this._readCardSubscription = this.smartCardCommunicationService.readCard()
      .pipe(
        switchMap(cardCode => {
          return this._addGuestCardProductToCart().pipe(mapTo(cardCode));
        }),
      ).subscribe(
        cardCode => {
          this._onCardDispenserCardRead(cardCode);
        },
        (error) => {
          const errorMessage = error && error.message ? error.message : error;
          this.loggerService.error(errorMessage);
          this.notificationService.show(PartnerCodeLoginComponent.name, errorMessage);
          this.loadingSpinnerService.hide();
        }
      );
  }

  private _abortProduceCard(): void {
    this._removeGuestCardProductFromCart();
  }

  private _onCardDispenserCardRead(code: string): void {
    this.scanPartnerCodeInfo = new ScanPartnerCodeInfo(null, ScanCard.createFromCardCode(code));
    this._setCurrentStep(PartnerCodeLoginSteps.GuestRegister);
    this.loadingSpinnerService.hide();
  }

  get canSaleGuestCard(): boolean {
    return !!this.guestCardProductId;
  }

  get guestCardProductId(): number {
    return this.shopSettingsService.shopSettings.guestCardProduct;
  }

  private _addGuestCardProductToCart(): Observable<boolean> {
    return this.productsStateService.depositProducts.pipe(
      map(products => {
        if (!products || !this.guestCardProductId) {
          return false;
        }
        const guestCardProduct = products.find(product => product.id === this.guestCardProductId);
        const order = this.orderStateService.order;
        if (!guestCardProduct || !order) {
          return false;
        }
        order.addOrderLine(
          OrderLineType.Deposits,
          guestCardProduct.name,
          guestCardProduct.id,
          guestCardProduct.price,
          guestCardProduct.taxRate
        );
        return true;
      }),
    );
  }

  private _removeGuestCardProductFromCart(): void {
    const order = this.orderStateService.order;
    if (!order || !order.orderLines || !this.guestCardProductId) {
      return;
    }

    const orderLine = order.orderLines.find(line => line.productId === this.guestCardProductId);
    if (orderLine) {
      order.deleteOrderLine(orderLine);
    }
  }

  get allowedLogin(): boolean {
    return this.shopSettingsService.allowedLogin;
  }

  get showPassword(): boolean {
    return this.shopSettingsService.showRegistrationPassword;
  }
}
